import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Megaphone, Plus, Trash2, Edit, Calendar, Users, FileText, 
  Download, X, Check, Eye, EyeOff, Upload, AlertCircle, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const Announcements = ({ readOnly = false }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    documents: [],
    target_employees: []
  });
  const [selectAllEmployees, setSelectAllEmployees] = useState(true);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [includeFuture, setIncludeFuture] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Duyurular yüklenemedi:', error);
      toast.error('Duyurular yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Filter only active employees
      const activeEmployees = response.data.filter(emp => emp.employment_status === 'active');
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Çalışanlar yüklenemedi:', error);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    fetchEmployees();
    fetchBranches();
  }, [fetchAnnouncements, fetchEmployees, fetchBranches]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      documents: [],
      target_employees: []
    });
    setSelectAllEmployees(true);
    setSelectedBranches([]);
    setIncludeFuture(true);
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedDocs = [...formData.documents];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const token = localStorage.getItem('fortis_token');
        const response = await axios.post(`${backendUrl}/api/upload`, formDataUpload, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedDocs.push({
          name: file.name,
          url: response.data.url,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Dosya yüklenemedi:', error);
        toast.error(`${file.name} yüklenemedi`);
      }
    }

    setFormData(prev => ({ ...prev, documents: uploadedDocs }));
    setUploading(false);
    e.target.value = '';
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Lütfen başlık giriniz');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Lütfen açıklama giriniz');
      return;
    }

    if (!selectAllEmployees && selectedBranches.length === 0 && formData.target_employees.length === 0) {
      toast.error('Lütfen en az bir şube veya çalışan seçin');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const submitData = buildSubmitData();

      await axios.post(`${backendUrl}/api/announcements`, submitData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Duyuru başarıyla yayınlandı');
      setShowAddModal(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Duyuru eklenemedi:', error);
      toast.error('Duyuru eklenirken hata oluştu');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!selectedAnnouncement) return;

    if (!selectAllEmployees && selectedBranches.length === 0 && formData.target_employees.length === 0) {
      toast.error('Lütfen en az bir şube veya çalışan seçin');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const submitData = buildSubmitData();

      await axios.put(`${backendUrl}/api/announcements/${selectedAnnouncement.id}`, submitData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Duyuru başarıyla güncellendi');
      setShowEditModal(false);
      resetForm();
      setSelectedAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      console.error('Duyuru güncellenemedi:', error);
      toast.error('Duyuru güncellenirken hata oluştu');
    }
  };

  // Gönderilecek hedefleme verisini oluştur
  const buildSubmitData = () => ({
    title: formData.title,
    description: formData.description,
    documents: formData.documents,
    scope: selectAllEmployees ? 'all' : 'custom',
    target_branches: selectAllEmployees ? [] : selectedBranches,
    target_employees: selectAllEmployees ? [] : formData.target_employees,
    include_future: includeFuture,
  });

  const handleDelete = async () => {
    if (!selectedAnnouncement) return;

    try {
      const token = localStorage.getItem('fortis_token');
      await axios.delete(`${backendUrl}/api/announcements/${selectedAnnouncement.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Duyuru başarıyla silindi');
      setShowDeleteModal(false);
      setSelectedAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      console.error('Duyuru silinemedi:', error);
      toast.error('Duyuru silinirken hata oluştu');
    }
  };

  const toggleActive = async (announcement) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const newStatus = !announcement.is_active;
      
      await axios.put(`${backendUrl}/api/announcements/${announcement.id}`, {
        is_active: newStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(newStatus ? 'Duyuru yayına alındı' : 'Duyuru pasife alındı');
      fetchAnnouncements();
    } catch (error) {
      console.error('Duyuru durumu değiştirilemedi:', error);
      toast.error('Duyuru durumu değiştirilirken hata oluştu');
    }
  };

  const openEditModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      description: announcement.description,
      documents: announcement.documents || [],
      target_employees: announcement.target_employees || []
    });
    const scope = announcement.scope ||
      ((!announcement.target_employees || announcement.target_employees.length === 0) &&
       (!announcement.target_branches || announcement.target_branches.length === 0) ? 'all' : 'custom');
    setSelectAllEmployees(scope === 'all');
    setSelectedBranches(announcement.target_branches || []);
    setIncludeFuture(announcement.include_future !== false);
    setShowEditModal(true);
  };

  const openDeleteModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDeleteModal(true);
  };

  const toggleEmployee = (employeeId) => {
    setFormData(prev => {
      const isSelected = prev.target_employees.includes(employeeId);
      return {
        ...prev,
        target_employees: isSelected
          ? prev.target_employees.filter(id => id !== employeeId)
          : [...prev.target_employees, employeeId]
      };
    });
  };

  const toggleBranch = (branchId) => {
    // Saf güncelleyici: updater içinde başka setState çağrılmaz (sonsuz döngü hatasının kökü)
    setSelectedBranches(prev =>
      prev.includes(branchId)
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    );
  };

  // Seçilen şubeler + tek tek seçilen çalışanların birleşik (benzersiz) sayısı
  const getSelectedEmployeeIds = () => {
    const branchEmpIds = employees
      .filter(emp => selectedBranches.includes(emp.branch_id))
      .map(emp => emp.id);
    return [...new Set([...branchEmpIds, ...formData.target_employees])];
  };

  // Liste kartında hedef etiketi
  const getTargetLabel = (a) => {
    const scope = a.scope ||
      ((!a.target_employees || a.target_employees.length === 0) &&
       (!a.target_branches || a.target_branches.length === 0) ? 'all' : 'custom');
    if (scope === 'all') return 'Tüm Çalışanlar';
    const parts = [];
    if (a.target_branches?.length) parts.push(`${a.target_branches.length} Şube`);
    if (a.target_employees?.length) parts.push(`${a.target_employees.length} Kişi`);
    return parts.join(' + ') || 'Özel';
  };

  const getEmployeesInBranch = (branchId) => {
    return employees.filter(emp => emp.branch_id === branchId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // data: URL'ler tarayıcıda doğrudan açılamaz/indirilemez; blob'a çevirip aç/indir
  const docToBlobUrl = async (doc) => {
    const res = await fetch(doc.url);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const handleViewDoc = async (doc) => {
    try {
      const blobUrl = await docToBlobUrl(doc);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error('Belge görüntülenemedi');
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const blobUrl = await docToBlobUrl(doc);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.name || 'belge';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error('Belge indirilemedi');
    }
  };

  // AnnouncementForm JSX renderer (inline, not a component - avoids re-mount on state change)
  const renderAnnouncementForm = (onSubmit, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Auto-filled info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-blue-800">FORTİS İNSAN KAYNAKLARI</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <Calendar className="h-4 w-4" />
          <span>{new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title" className="text-base font-semibold">
          Başlık <span className="text-red-500">*</span>
        </Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Duyuru başlığını giriniz"
          className="text-lg"
          required
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-base font-semibold">
          Açıklama <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Duyuru içeriğini yazınız..."
          className="min-h-[150px]"
          required
        />
      </div>

      {/* Documents */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Belgeler</Label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
          <input
            type="file"
            id="document-upload"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          />
          <label
            htmlFor="document-upload"
            className="flex flex-col items-center gap-2 cursor-pointer"
          >
            <Upload className={`h-8 w-8 ${uploading ? 'animate-bounce text-blue-500' : 'text-gray-400'}`} />
            <span className="text-sm text-gray-600">
              {uploading ? 'Yükleniyor...' : 'Belge yüklemek için tıklayın'}
            </span>
            <span className="text-xs text-gray-400">PDF, Word, Excel, PNG, JPG</span>
          </label>
        </div>
        
        {formData.documents.length > 0 && (
          <div className="space-y-2">
            {formData.documents.map((doc, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">{doc.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDocument(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Target Employees */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Kimlere Gösterilsin?</Label>
        
        {/* Tüm Çalışanlara Göster */}
        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
          <Checkbox
            id="select-all"
            checked={selectAllEmployees}
            onCheckedChange={(checked) => {
              setSelectAllEmployees(checked);
              if (checked) {
                setSelectedBranches([]);
                setFormData(prev => ({ ...prev, target_employees: [] }));
              }
            }}
          />
          <label htmlFor="select-all" className="text-sm font-medium text-green-800 cursor-pointer flex items-center gap-2">
            <Users className="h-4 w-4" />
            Tüm Çalışanlara Göster
          </label>
        </div>

        {!selectAllEmployees && (
          <>
            {/* Branch Selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-600" />
                Şubelere Göre Seç
              </p>
              <div className="flex flex-wrap gap-2">
                {branches.map(branch => {
                  const branchEmployeeCount = getEmployeesInBranch(branch.id).length;
                  const isSelected = selectedBranches.includes(branch.id);
                  return (
                    <div
                      key={branch.id}
                      onClick={() => toggleBranch(branch.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all border ${
                        isSelected 
                          ? 'bg-indigo-100 border-indigo-400 text-indigo-800' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className={`flex h-4 w-4 items-center justify-center rounded border shrink-0 ${
                        isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="text-sm font-medium">{branch.name}</span>
                      <Badge variant={isSelected ? "default" : "secondary"} className="text-xs">
                        {branchEmployeeCount}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Individual Employee Selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                Veya Tek Tek Seç
              </p>
              <div className="max-h-[200px] overflow-y-auto border rounded-lg p-3 space-y-2">
                {employees.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Çalışan bulunamadı</p>
                ) : (
                  employees.map(emp => (
                    <div 
                      key={emp.id} 
                      className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                        formData.target_employees.includes(emp.id) 
                          ? 'bg-blue-100 border border-blue-300' 
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => toggleEmployee(emp.id)}
                    >
                      <span className={`flex h-4 w-4 items-center justify-center rounded border shrink-0 ${
                        formData.target_employees.includes(emp.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                      }`}>
                        {formData.target_employees.includes(emp.id) && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="text-sm">{emp.name}</span>
                      {emp.branch_name && (
                        <Badge variant="outline" className="ml-auto text-xs">
                          {emp.branch_name}
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
        
        {!selectAllEmployees && getSelectedEmployeeIds().length > 0 && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
            <Check className="h-4 w-4 text-blue-600" />
            <p className="text-sm text-blue-700 font-medium">
              {getSelectedEmployeeIds().length} çalışan seçildi
              {selectedBranches.length > 0 && ` (${selectedBranches.length} şube)`}
            </p>
          </div>
        )}
      </div>

      {/* İleride gelen kullanıcılar da görsün */}
      <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <Checkbox
          id="include-future"
          checked={includeFuture}
          onCheckedChange={(checked) => setIncludeFuture(!!checked)}
          data-testid="include-future-checkbox"
        />
        <label htmlFor="include-future" className="cursor-pointer">
          <span className="text-sm font-medium text-amber-800">İleride gelen kullanıcılar da görsün</span>
          <p className="text-xs text-amber-700 mt-0.5">
            Kapalıysa bu duyuru yalnızca şu an mevcut çalışanlara gösterilir; sonradan eklenen yeni kullanıcılar görmez.
          </p>
        </label>
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => { isEdit ? setShowEditModal(false) : setShowAddModal(false); resetForm(); }}
        >
          İptal
        </Button>
        <Button 
          type="submit" 
          className="bg-blue-600 hover:bg-blue-700"
          disabled={uploading}
        >
          <Megaphone className="h-4 w-4 mr-2" />
          {isEdit ? 'Güncelle' : 'Yayınla'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
            <Megaphone className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Duyurular</h1>
            <p className="text-sm text-gray-500">Şirket duyurularını yönetin</p>
          </div>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md"
          data-testid="add-announcement-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Duyuru Ekle
        </Button>
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : announcements.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Megaphone className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Henüz duyuru yok</h3>
            <p className="text-sm text-gray-500 mb-4">İlk duyurunuzu oluşturun</p>
            <Button 
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Duyuru Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card 
              key={announcement.id} 
              className={`hover:shadow-lg transition-shadow border-l-4 ${
                announcement.is_active !== false 
                  ? 'border-l-blue-500' 
                  : 'border-l-gray-400 opacity-75 bg-gray-50'
              }`}
              data-testid={`announcement-${announcement.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className={`text-xl ${announcement.is_active !== false ? 'text-gray-900' : 'text-gray-500'}`}>
                        {announcement.title}
                      </CardTitle>
                      {announcement.is_active === false && (
                        <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Pasif
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {announcement.published_by}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(announcement.published_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {getTargetLabel(announcement)}
                      </span>
                      {announcement.include_future === false && (
                        <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
                          Yeni kullanıcılar hariç
                        </Badge>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Pasife Al / Yayına Al Toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(announcement)}
                      className={announcement.is_active !== false 
                        ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-200' 
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200'}
                      data-testid={`toggle-announcement-${announcement.id}`}
                    >
                      {announcement.is_active !== false ? (
                        <>
                          <EyeOff className="h-4 w-4 mr-1" />
                          Pasife Al
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-1" />
                          Yayına Al
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(announcement)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`edit-announcement-${announcement.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteModal(announcement)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-announcement-${announcement.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className={`whitespace-pre-wrap ${announcement.is_active !== false ? 'text-gray-700' : 'text-gray-500'}`}>
                  {announcement.description}
                </p>
                
                {announcement.documents && announcement.documents.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">Ekli Belgeler:</p>
                    <div className="flex flex-wrap gap-2">
                      {announcement.documents.map((doc, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-blue-700"
                        >
                          <FileText className="h-4 w-4" />
                          <span className="text-sm">{doc.name}</span>
                          <button
                            type="button"
                            onClick={() => handleViewDoc(doc)}
                            className="ml-1 p-1 rounded hover:bg-blue-100 transition-colors"
                            title="Görüntüle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadDoc(doc)}
                            className="p-1 rounded hover:bg-blue-100 transition-colors"
                            title="İndir"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Megaphone className="h-5 w-5 text-blue-600" />
              Yeni Duyuru
            </DialogTitle>
            <DialogDescription>
              Çalışanlara duyuru yayınlayın
            </DialogDescription>
          </DialogHeader>
          {renderAnnouncementForm(handleSubmit, false)}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Edit className="h-5 w-5 text-blue-600" />
              Duyuru Düzenle
            </DialogTitle>
            <DialogDescription>
              Duyuru bilgilerini güncelleyin
            </DialogDescription>
          </DialogHeader>
          {renderAnnouncementForm(handleUpdate, true)}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Duyuru Sil
            </DialogTitle>
            <DialogDescription>
              "{selectedAnnouncement?.title}" duyurusunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              İptal
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-announcement"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Announcements;
